import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { inr, imgSrc } from "@/api";
import { useCart } from "@/context/CartContext";

const ProductCard = ({ product, index = 0 }) => {
  const { add } = useCart();
  const [quickAdd, setQuickAdd] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);

  const availableSizes = (product.sizes || []).filter((s) => (product.stock?.[s] ?? 0) > 0);
  const soldOut = availableSizes.length === 0;

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soldOut) return;
    setQuickAdd(true);
    setSelectedSize(null);
  };

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedSize) return;
    add(product, selectedSize, 1);
    setQuickAdd(false);
    setSelectedSize(null);
  };

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
      className="group"
    >
      <div className="relative">
        <Link to={`/product/${product.id}`} data-testid={`product-link-${product.id}`} className="block">
          <div className="relative overflow-hidden ring-1 ring-gold/15 bg-ivory-deep aspect-[4/5]">
            {product.badge && (
              <span className="absolute top-4 left-4 z-10 bg-ivory-card/90 backdrop-blur px-3 py-1 text-[9px] tracking-[0.3em] uppercase text-gold-dark border border-gold/40">
                {product.badge}
              </span>
            )}
            {soldOut && (
              <span data-testid={`sold-out-${product.id}`} className="absolute top-4 right-4 z-10 bg-maroon px-3 py-1 text-[9px] tracking-[0.3em] uppercase text-ivory">
                Sold Out
              </span>
            )}
            <img
              src={imgSrc(product.images[0])}
              alt={product.name}
              loading={index < 4 ? "eager" : "lazy"}
              fetchPriority={index < 2 ? "high" : "auto"}
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-105 group-hover:brightness-[1.06]"
            />
            <img
              src={imgSrc(product.images[1] || product.images[0])}
              alt={`${product.name} detail`}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover opacity-0 transition-all duration-700 group-hover:opacity-100 group-hover:scale-105"
            />
            <div className="hidden sm:flex absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-ivory-card/90 backdrop-blur border-t border-gold/30 px-5 py-3 items-center justify-between">
              <span className="text-[10px] tracking-[0.3em] uppercase text-maroon-deep">View the piece</span>
              <ArrowRight className="w-4 h-4 text-gold-dark" />
            </div>
          </div>
        </Link>

        {!soldOut && (
          <button
            type="button"
            data-testid={`quick-add-${product.id}`}
            onClick={handleQuickAdd}
            className="sm:hidden absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-ivory-card/95 backdrop-blur border border-gold/40 px-5 py-2.5 text-[9px] tracking-[0.28em] uppercase text-maroon-deep shadow-sm whitespace-nowrap"
          >
            Quick Add +
          </button>
        )}

        {quickAdd && (
          <div
            data-testid={`quick-add-panel-${product.id}`}
            className="sm:hidden absolute inset-x-2 bottom-2 z-30 bg-ivory-card/98 border border-gold/40 p-3 shadow-lg"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[9px] tracking-[0.28em] uppercase text-gold-dark">Select Size</span>
              <button type="button" onClick={() => setQuickAdd(false)} className="text-[10px] text-clay">Close</button>
            </div>
            <div className="flex gap-2">
              {availableSizes.map((s) => (
                <button
                  type="button"
                  key={s}
                  data-testid={`quick-size-${product.id}-${s}`}
                  onClick={() => setSelectedSize(s)}
                  className={`w-10 h-10 border text-xs transition-colors ${
                    selectedSize === s ? "bg-maroon text-ivory border-maroon" : "border-maroon/20 text-maroon-deep hover:border-gold"
                  }`}
                >
                  {s}
                </button>
              ))}
              <button
                type="button"
                disabled={!selectedSize}
                data-testid={`quick-add-confirm-${product.id}`}
                onClick={handleAdd}
                className="ml-auto px-3 bg-maroon text-ivory text-[9px] tracking-[0.2em] uppercase disabled:opacity-40 flex items-center gap-2"
              >
                <Check className="w-3 h-3" /> Add
              </button>
            </div>
          </div>
        )}
      </div>

      <Link to={`/product/${product.id}`} className="block pt-4 sm:pt-5 flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-4">
        <div className="min-w-0">
          <p className="text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-gold-dark">{product.category_label}</p>
          <h3 className="font-display text-lg sm:text-2xl text-maroon-deep leading-tight mt-1 group-hover:text-maroon transition-colors">
            {product.name}
          </h3>
        </div>
        <p data-testid={`product-price-${product.id}`} className="font-display text-lg text-maroon-deep whitespace-nowrap sm:pt-4">
          {inr(product.price)}
        </p>
      </Link>
    </motion.article>
  );
};

export default ProductCard;
